import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import cityService from '../services/CityService';
import useMarkerSelection from '../hooks/useMarkerSelection';

export const CityContext = createContext();

export const CityProvider = ({ children }) => {
    const { cityId } = useParams();
    const [searchParams] = useSearchParams();
    const [city, setCity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isChecked, setIsChecked] = useState(null);
    const [currentCheckedChangeId, setCurrentCheckedChangeId] = useState(null);
    const [addMarker, setAddMarker] = useState(null);
    const [editMarker, setEditMarker] = useState(null);
    const currentMapLayerElement = useRef({}).current;
    const actualMarkerPos = useRef({}).current;
    const allMarkers = useRef([]).current;
    const [selectedMarker, setSelectedMarker] = useMarkerSelection(cityId, setError);
    const cityRequest = useRef(null);

    // Fetch city function - extracted for reuse
    const fetchCity = useCallback(async () => {
        if (!cityId) return;
        cityRequest.current?.abort();
        const controller = new AbortController();
        cityRequest.current = controller;
        try {
            setLoading(true);
            const cityData = await cityService.getCityById(cityId, controller.signal);
            if (controller.signal.aborted) return;
            let checkData = {};
            for (let layer of cityData.layers) {
                // If the layer type is 'Satellite', set it to false by default.
                // Otherwise (Cartoon or no type), keep it true.
                const isSatelliteLayer = layer.type === 'Satellite';
                checkData[layer._id] = isSatelliteLayer ? false : true;
                for (let marker of layer.markers) {
                    checkData[marker._id] = isSatelliteLayer ? false : true;
                }
            }
            cityData.isChecked = checkData;
            setIsChecked(checkData);
            setCity(cityData);
            setError(null);
        } catch (err) {
            if (!controller.signal.aborted) setError(err.message);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [cityId]);

    // Refresh function to be used by components
    const refreshCity = useCallback(async () => {
        await fetchCity();
    }, [fetchCity]);

    // Refresh only markers (lighter refresh if you only need to update markers)
    const refreshMarkers = useCallback(async () => {
        if (!cityId || !city) return;
        cityRequest.current?.abort();
        const controller = new AbortController();
        cityRequest.current = controller;
        try {
            const cityData = await cityService.getCityById(cityId, controller.signal);
            if (controller.signal.aborted) return;
            
            // Update the city data while preserving other state
            setCity(prevCity => ({
                ...prevCity,
                layers: cityData.layers
            }));
            
            // Make sure new layers and markers get an entry in isChecked
            // otherwise they won't render on the map
            setIsChecked(prev => {
                const newCheckData = { ...prev };
                for (let layer of cityData.layers) {
                    const isSatelliteLayer = layer.type === 'Satellite';
                    if (newCheckData[layer._id] === undefined) {
                        newCheckData[layer._id] = !isSatelliteLayer;
                    }
                    for (let marker of layer.markers) {
                        if (newCheckData[marker._id] === undefined) {
                            newCheckData[marker._id] = !isSatelliteLayer;
                        }
                    }
                }
                return newCheckData;
            });
            
            // Update selectedMarker if it exists to reflect any changes
            if (selectedMarker) {
                const updatedSelectedMarker = cityData.layers
                    .flatMap(layer => layer.markers)
                    .find(marker => marker._id === selectedMarker._id);
                
                if (updatedSelectedMarker) {
                    setSelectedMarker(updatedSelectedMarker);
                }
            }
        } catch (err) {
            if (controller.signal.aborted) return;
            console.error('Error refreshing markers:', err);
            setError(err.message);
        }
    }, [cityId, city, selectedMarker]);

    useEffect(() => {
        setCity(null);
        setIsChecked(null);
        fetchCity();
        return () => cityRequest.current?.abort();
    }, [fetchCity]);

    // Auto-open edit form when navigated with query params (e.g. from MarkerTableModal)
    useEffect(() => {
        if (!city) return;
        const editMarkerId = searchParams.get('editMarkerId');
        const editLayerId = searchParams.get('editLayerId');
        if (editMarkerId && editLayerId) {
            const layer = city.layers.find(l => l._id === editLayerId);
            const marker = layer?.markers.find(m => m._id === editMarkerId);
            if (marker) {
                setSelectedMarker({ ...marker, layerId: editLayerId });
                setEditMarker({
                    status: true,
                    selected: true,
                    markerId: editMarkerId,
                    layerId: editLayerId
                });
            }
        }
    }, [city, searchParams]);

    return (
        <CityContext.Provider value={{
            currentMapLayerElement,
            actualMarkerPos,
            allMarkers,
            city,
            setCity,
            loading,
            error,
            isChecked,
            setIsChecked,
            currentCheckedChangeId,
            setCurrentCheckedChangeId,
            addMarker,
            setAddMarker,
            editMarker,
            setEditMarker,
            selectedMarker,
            setSelectedMarker,
            refreshCity,
            refreshMarkers
        }}>
            {children}
        </CityContext.Provider>
    );
};
